import { useMemo, type JSX } from "react";

import {
  useProgressContext,
  Progress as ArkProgress,
} from "@ark-ui/react/progress";

import { CompositionError } from "../../../features/utils/composition";

type ProgressValueTextProps = {
  className?: string;
  _type?: "circular" | "linear";
} & (
  | {
      style?: "raw" | "percent";
      render?: never;
    }
  | {
      style: "step";
      render?: never;
    }
  | {
      style: "custom";
      render: (value: number | null) => string;
    }
);

export function ProgressValueText({
  render,
  className,
  _type: type,
  style = "raw",
}: ProgressValueTextProps): JSX.Element {
  const machine = useProgressContext();

  if (!type) {
    throw new CompositionError(
      "Progress.ValueText",
      "Progress.Circular | Progress.Linear",
    );
  }

  const content = useMemo(() => {
    if (style === "percent") {
      return `${machine.value}%`;
    }
    if (style === "step") {
      return `${machine.value}/${machine.max}`;
    }
    if (style === "custom" && render) {
      return render(machine.value);
    }
    return machine.value;
  }, [machine.max, machine.value, render, style]);

  if (type === "circular") {
    return (
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        className={className}
        dominantBaseline="middle"
      >
        {content}
      </text>
    );
  }

  return (
    <ArkProgress.ValueText className={className}>
      {content}
    </ArkProgress.ValueText>
  );
}
